import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import EndPoints from "../../../services/end-points";
import { IBatch } from "../../../models/wms/batch/response";
import { IRawMaterialPick } from "../../../models/wms/batch/request";
import FormItem from "../../general/form-item";

export interface RawConsumption {
  quantity: number | null;
  picks: IRawMaterialPick[];
}

interface Props {
  rawMaterialId: number;
  storeId: number;
  value: RawConsumption;
  onChange: (value: RawConsumption) => void;
}

export default function RawMaterialConsumption({
  rawMaterialId,
  storeId,
  value,
  onChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const [batches, setBatches] = useState<IBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [available, setAvailable] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [weighted, setWeighted] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    setAvailable(null);
    setBatches([]);
    EndPoints.wmsBatch
      .getAllBatches({
        productId: rawMaterialId,
        storeId,
        perPage: -1,
        hasStock: true,
      })
      .then(({ data }) => {
        if (!active) return;
        setBatches(data.data);
        setAvailable(data.availableQuantity ?? null);
        setName(data.productName ?? "");
        setWeighted(Boolean(data.productSoldByUnit));
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [rawMaterialId, storeId, refresh, i18n.language]);
  const unit = t(weighted ? "manufacturing_grams" : "manufacturing_pieces");
  const total = value.picks.reduce((sum, pick) => sum + pick.quantity, 0);
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Text strong>
        {t("raw_material")}: {name || `#${rawMaterialId}`}
      </Typography.Text>
      <FormItem label={`${t("manufacturing_raw_consumed")} (${unit})`} required>
        <InputNumber
          aria-label={t("manufacturing_raw_consumed")}
          min={1}
          precision={0}
          value={value.quantity}
          onChange={(quantity) => onChange({ ...value, quantity })}
          style={{ width: "100%" }}
        />
        <Typography.Text type="secondary">
          {t("available")}: {loading ? "…" : (available ?? "—")} {unit}
        </Typography.Text>
      </FormItem>
      {failed ? (
        <Alert
          type="error"
          message={t("manufacturing_load_failed")}
          action={
            <Button
              size="small"
              onClick={() => setRefresh((current) => current + 1)}
            >
              {t("manufacturing_refresh")}
            </Button>
          }
        />
      ) : null}
      {available !== null && Number(value.quantity) > available ? (
        <Alert type="error" message={t("manufacturing_insufficient")} />
      ) : null}
      <FormItem label={t("manufacturing_raw_batches_optional")}>
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          loading={loading}
          aria-label={t("manufacturing_raw_batches_optional")}
          style={{ width: "100%" }}
          placeholder={t("manufacturing_automatic")}
          value={value.picks.map((pick) => pick.batchId)}
          options={batches.map((batch) => ({
            value: batch.id,
            label: `${batch.batchNumber || `#${batch.id}`} / ${batch.expirationDate || t("batch_has_no_expiration_date")} / ${t("available")}: ${batch.availableQuantity} ${unit}`,
          }))}
          onChange={(ids: number[]) =>
            onChange({
              ...value,
              picks: ids.map(
                (batchId) =>
                  value.picks.find((pick) => pick.batchId === batchId) ?? {
                    batchId,
                    quantity: 0,
                  },
              ),
            })
          }
        />
        <Typography.Text type="secondary">
          {t("manufacturing_fefo_hint")}
        </Typography.Text>
      </FormItem>
      {value.picks.length ? (
        <>
          <Table
            size="small"
            pagination={false}
            rowKey="batchId"
            dataSource={value.picks}
            columns={[
              {
                title: t("batch_number"),
                render: (_: unknown, pick: IRawMaterialPick) =>
                  batches.find((batch) => batch.id === pick.batchId)
                    ?.batchNumber || `#${pick.batchId}`,
              },
              {
                title: `${t("quantity")} (${unit})`,
                render: (_: unknown, pick: IRawMaterialPick) => (
                  <InputNumber
                    min={1}
                    precision={0}
                    value={pick.quantity || null}
                    aria-label={`${t("quantity")} #${pick.batchId}`}
                    max={
                      batches.find((batch) => batch.id === pick.batchId)
                        ?.availableQuantity
                    }
                    onChange={(quantity) =>
                      onChange({
                        ...value,
                        picks: value.picks.map((item) =>
                          item.batchId === pick.batchId
                            ? { ...item, quantity: quantity ?? 0 }
                            : item,
                        ),
                      })
                    }
                  />
                ),
              },
            ]}
          />
          <Typography.Text
            type={total !== value.quantity ? "danger" : undefined}
          >
            {t("manufacturing_selected_total")}: {total} /{" "}
            {value.quantity ?? "—"} {unit}
          </Typography.Text>
        </>
      ) : null}
    </Space>
  );
}
